import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Permission, User, UserType } from '../../app/core/model/user.model';

@Injectable({
    providedIn: 'root'
})
export class FlamingoAuthService {
    private _token: string | null = null;
    private _localStorage = window.localStorage;
    private _sessionStorage = window.sessionStorage;
    private _storage = sessionStorage;
    private _storagePrefix = 'flamingo_';

    constructor(
        private _jwtHelper: JwtHelperService,
    ) {
    }

    private _refreshToken: string | null = null;

    get refreshToken(): string | null {
        if (!this._refreshToken) {
            this._refreshToken = this._storage.getItem(this._storagePrefix + 'refresh_token');
        }
        return this._refreshToken;
    }

    set refreshToken(value: string | null) {
        if (value) {
            this._storage.setItem(this._storagePrefix + 'refresh_token', value);
        } else {
            this._storage.removeItem(this._storagePrefix + 'refresh_token');
        }
        this._refreshToken = null;
    }

    private _user: User | null = null;

    get user(): User | null {
        return this._user;
    }

    set user(value: User | null) {
        this._user = value;
    }

    public decodeJwtToken(token: string): any {
        return this._jwtHelper.decodeToken(token);
    }

    public rememberMe(rememberMe = false): void {
        this._storage = this._sessionStorage;
        if (rememberMe) {
            this._storage = this._localStorage;
        }
    }

    public getToken(): string | null {
        if (!this._token) {
            this._token = this._storage.getItem(this._storagePrefix + 'token');

            if (!this._token) {
                this._storage = this._localStorage;
                this._token = this._storage.getItem(this._storagePrefix + 'token');
            }
        }
        return this._token;
    }

    public setToken(token: string): void {
        this._storage.setItem(this._storagePrefix + 'token', token);
        this._refreshToken = null;
        this.updateUserFromToken();
    }

    public updateUserFromToken(): void {
        const token = this.getToken();
        if (token) {
            const decodedToken = this._jwtHelper.decodeToken(token);
            this.user = {
                id: decodedToken.id,
                email: decodedToken.email,
                company: decodedToken.company,
                type: decodedToken.type,
                createdAt: decodedToken.iat,
                expiredAt: decodedToken.exp,
                username: decodedToken.username,
                permissions: decodedToken.perm,
                partnerPermissions: decodedToken.pperm,
            } as User;
        }
    }

    public resetToken(): void {
        this._storage.clear();
    }

    hasUserPermissions = (requiredPermissions: Permission | Permission[]): boolean => {
        if (!this.user) return false;
        const perm = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
        return this.user.permissions.some(p => [Permission.All, ...perm].includes(p));
    };

    can(type: UserType | UserType[]): boolean {
        if (!this.user) return false;
        if (!Array.isArray(type)) {
            type = [type];
        }
        return type.every(p => this.user?.type === p);
    }

    cannot(type: UserType | UserType[]): boolean {
        if (!this.user) return true;
        if (!Array.isArray(type)) {
            type = [type];
        }

        return !type.every(p => this.user?.type === p);
    }

    public logout(): void {
        this.resetToken();
        this._token = null;
        this._refreshToken = null;
        this._user = null;
    }

    public isLogged(): boolean {
        return !!this._user;
    }

    public setNavigation(): void {
        // Navigation logic based on user type
        console.log('🔧 Setting navigation for user type:', this.user?.type);
    }

    public setMockUser(): void {
        // Mock user for testing
        this.user = {
            id: 'mock-user-id',
            email: 'test@example.com',
            company: 'Test Company',
            type: UserType.debtor,
            createdAt: Date.now(),
            expiredAt: Date.now() + 86400000, // 24 hours
            username: 'testuser',
            permissions: [Permission.All],
            partnerPermissions: []
        } as User;
        console.log('🔧 Mock user set:', this.user);
    }
}
